import * as React from 'react';
import { useEffect, useState } from 'react';
import { useCommonUIActionsContext } from '../../../contexts/CommonUIActionsContext';
import { usePlayerPortalsConfigContext } from '../../../contexts';
import useFetchOnSiteRegistrationData from '../../../hooks/useFetchOnSiteRegistrationData';
import {
  SEARCH_PLACEHOLDER,
  NO_DATA_FOUND_LABEL,
  ON_SITE_REGISTRATION_PLAYERS_DATA_SOURCE,
  ON_SITE_REGISTRATION_CADDIES_DATA_SOURCE,
} from '@r-and-a-shared-ui/common-ui/src/constants';
import ParticipantCard from './components/ParticipantCard';
import RegistrationForm from './components/RegistrationForm';
import FormControl from '@mui/material/FormControl';
import NativeSelect, { SelectChangeEvent } from '@mui/material/Select';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import InputLabel from '@mui/material/InputLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dashboard from './components/Dashboard';
import { Elements } from '@kontent-ai/delivery-sdk';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { SidePanel } from '../../SidePanel/SidePanel';
import { EmailConfig, SmsConfig } from './helpers/communicationHelpers';
import { CreateCaddieForm } from './components/CreateCaddieForm';

import styles from './PlayerPortalsOnSiteRegistrationAdminPanel.module.scss';
import { CTA } from '../../CTA/CTA';
import { SimplePopUp } from '../../SimplePopUp/SimplePopUp';
import { ConfirmationDialog } from 'libs/common-forms-ui/src/lib/PlayerPortals/ConfirmationDialog/ConfirmationDialog';

export const CircularProgressSize = 24;
export const StackSpacing = 2;

type AdminPanelProps = {
  title?: string;
  pageSize?: number;
  actionBtnLabels: {
    reSendSMSBtnLabel: string;
    reSendEmailBtnLabel: string;
    agreeToPoliciesBtnLabel: string;
    completeRegistrationBtnLabel: string;
  };
  policies: {
    policySectionTitle: string;
    antiDopingPolicyText: string;
    antiBettingPolicyText: string;
    privacyPolicySectionText?: Elements.RichTextElement;
  };
  registrationForm: any;
  createCaddieForm: any;
  dataSource: string;
  dashboard: {
    registeredLabel: string;
    notRegisteredLabel: string;
    dataFilledInAndNotRegisteredLabel: string;
  };
  communicationConfigs?: {
    smsConfig?: SmsConfig;
    emailConfig?: EmailConfig;
  };
  kontentItemId?: string;
};

export const PlayerPortalsOnSiteRegistrationAdminPanel = (
  props: AdminPanelProps,
) => {
  const {
    title,
    pageSize = 16,
    policies,
    dataSource,
    registrationForm,
    createCaddieForm,
    actionBtnLabels,
    dashboard,
    communicationConfigs,
    kontentItemId,
  } = props;

  const isPlayersDataSource =
    dataSource?.toLowerCase() === ON_SITE_REGISTRATION_PLAYERS_DATA_SOURCE;
  const isCaddiesDataSource =
    dataSource?.toLowerCase() === ON_SITE_REGISTRATION_CADDIES_DATA_SOURCE;

  const [allParticipants, setAllParticipants] = React.useState<any[]>([]);
  const [allPlayers, setAllPlayers] = React.useState<any[]>([]);

  const [displayedParticipants, setDisplayedParticipants] = React.useState<
    any[]
  >([]);
  const [selectedParticipant, setSelectedParticipant] = useState<object | null>(
    null,
  );

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCreateCaddieFormOpen, setIsCreateCaddieFormOpen] = useState(false);
  const [isCloseCaddieConfirmOpen, setIsCloseCaddieConfirmOpen] =
    useState(false);

  const [statusFilter, setStatusFilter] = React.useState<number>(3);
  const [searchQuery, setSearchQuery] = useState('');

  const actionContext = useCommonUIActionsContext();
  const { onSiteRegistrationAPI } = actionContext || {};

  const configContext = usePlayerPortalsConfigContext();
  const { currentChampionship } = configContext || {};

  const [page, setPage] = useState(1);

  const paginatedParticipants = React.useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return displayedParticipants.slice(startIndex, startIndex + pageSize);
  }, [displayedParticipants, page]);

  const { playersData, caddiesData, loading, hasFetched, refetch } =
    useFetchOnSiteRegistrationData(
      onSiteRegistrationAPI,
      currentChampionship?.championshipIdentifier,
      dataSource,
    );

  useEffect(() => {
    if (!isPanelOpen && hasFetched) {
      refetch?.();
    }
  }, [isPanelOpen]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (event: SelectChangeEvent<number>) => {
    setStatusFilter(Number(event.target.value));
    setPage(1);
  };

  useEffect(() => {
    if (isPlayersDataSource && playersData?.championshipPlayers) {
      setAllParticipants(playersData.championshipPlayers);
      setDisplayedParticipants(playersData.championshipPlayers);
    } else if (isCaddiesDataSource) {
      if (caddiesData?.championshipCaddies) {
        setAllParticipants(caddiesData.championshipCaddies);
        setDisplayedParticipants(caddiesData.championshipCaddies);
      }
      if (playersData?.championshipPlayers) {
        setAllPlayers(playersData.championshipPlayers);
      }
    }
  }, [playersData, caddiesData]);

  useEffect(() => {
    let filtered = allParticipants;

    if (statusFilter !== 3) {
      filtered = filtered.filter(
        participant => participant.registrationStatus?.value === statusFilter,
      );
    }

    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(participant =>
        participant.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    setDisplayedParticipants(filtered);
    setPage(1);
  }, [allParticipants, statusFilter, searchQuery]);

  if (loading || !hasFetched) {
    return (
      <div className={styles.fetchStatus}>
        <CircularProgress size={CircularProgressSize} />
      </div>
    );
  }

  if (allParticipants.length === 0) {
    return <div className={styles.fetchStatus}>{NO_DATA_FOUND_LABEL}</div>;
  }

  const onClickNewCaddieHandler = () => {
    setIsCreateCaddieFormOpen(true);
  };

  const onCloseFormModal = () => {
    setIsCloseCaddieConfirmOpen(false);
    setIsCreateCaddieFormOpen(false);
    refetch?.();
  };

  const onRequestCloseFormModal = () => {
    setIsCloseCaddieConfirmOpen(true);
  };

  const onCancelCloseFormModal = () => {
    setIsCloseCaddieConfirmOpen(false);
  };

  return (
    <div className={styles.container} data-kontent-item-id={kontentItemId}>
      <Dashboard
        registeredLabel={dashboard.registeredLabel}
        notRegisteredLabel={dashboard.notRegisteredLabel}
        dataFilledInAndNotRegisteredLabel={
          dashboard.dataFilledInAndNotRegisteredLabel
        }
        participants={allParticipants}
      />

      <div className={styles.toolbar}>
        {title && <h1 className={styles.title}>{title}</h1>}
        <div className={styles.controls}>
          {isCaddiesDataSource && (
            <CTA
              onClick={onClickNewCaddieHandler}
              label="+ Add Caddie"
              isDisabled={!hasFetched}
            />
          )}

          <FormControl
            sx={{
              minWidth: 200,
              marginTop: '10px',
            }}
            size="small"
            className={styles.filterButton}
          >
            <InputLabel className={styles.filterLabel}>
              Registration Status
            </InputLabel>
            <NativeSelect
              value={statusFilter}
              label="Registration Status"
              onChange={handleFilterChange}
              className={styles.filterMenu}
            >
              <MenuItem value={3}>All</MenuItem>
              <MenuItem value={0}>Not Registered</MenuItem>
              <MenuItem value={1}>Pre-Registration Complete</MenuItem>
              <MenuItem value={2}>Registered</MenuItem>
            </NativeSelect>
          </FormControl>

          <div className={styles.searchBox}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <input
              type="text"
              placeholder={SEARCH_PLACEHOLDER}
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
          </div>
        </div>
      </div>

      {paginatedParticipants.length === 0 ? (
        <div className={styles.fetchStatus}>{NO_DATA_FOUND_LABEL}</div>
      ) : (
        <div className={styles.participantsContainer}>
          <div className={styles.participantsList}>
            {paginatedParticipants.map(participant => (
              <ParticipantCard
                key={participant.id}
                name={participant.name}
                registrationStatus={participant.registrationStatus?.value}
                caddieName={
                  isPlayersDataSource
                    ? undefined
                    : participant.championshipPlayer.name
                }
                handleCardClick={() => {
                  setSelectedParticipant(participant);
                  setIsPanelOpen(true);
                }}
              />
            ))}
          </div>
          {displayedParticipants.length > pageSize && (
            <div className={styles.pagination}>
              <Stack spacing={StackSpacing}>
                <Pagination
                  color="primary"
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  count={Math.ceil(displayedParticipants.length / pageSize)}
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </div>
          )}
        </div>
      )}

      {isPanelOpen && selectedParticipant && (
        <SidePanel
          children={
            <RegistrationForm
              key={(selectedParticipant as any)?.id}
              participantId={(selectedParticipant as any)?.id}
              registrationForm={registrationForm}
              championshipPolicies={policies}
              actionBtnLabels={actionBtnLabels}
              isPlayersDataSource={isPlayersDataSource}
              communicationConfigs={communicationConfigs}
            />
          }
          isPanelOpen={isPanelOpen}
          setIsPanelOpen={setIsPanelOpen}
          items={allParticipants}
          selectedItem={selectedParticipant}
          setSelectedItem={setSelectedParticipant}
        />
      )}

      {isCaddiesDataSource && (
        <>
          <SimplePopUp
            isVisible={isCreateCaddieFormOpen}
            onClose={onRequestCloseFormModal}
          >
            <CreateCaddieForm
              allPlayers={allPlayers}
              createCaddieForm={createCaddieForm}
              onFormClosed={onCloseFormModal}
            />
          </SimplePopUp>

          <ConfirmationDialog
            isDialogOpen={isCloseCaddieConfirmOpen}
            onConfirm={onCloseFormModal}
            onClose={onCancelCloseFormModal}
          />
        </>
      )}
    </div>
  );
};

export default PlayerPortalsOnSiteRegistrationAdminPanel;
